import { html, cx, useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from './h.js'
import { Icon } from './icons.js'
import { initials, avatarColor, copyText } from './format.js'
import { t } from './i18n.js'
import { CATEGORIES, searchEmoji, loadRecent, pushRecent } from './emoji.js'

/* ------------------------------------------------------------------ *
 * Avatar
 * ------------------------------------------------------------------ */

export function Avatar ({ name, id, src, size = '', online = false, square = false, onClick = null }) {
  const label = initials(name, id)
  const body = src
    ? html`<img src=${src} alt="" />`
    : html`<span>${label}</span>`

  return html`
    <div class=${cx('avatar', size && 'avatar-' + size, square && 'square')}
         style=${src ? null : { background: avatarColor(id || name || '') }}
         onClick=${onClick}
         role=${onClick ? 'button' : null}
         title=${name || ''}>
      ${body}
      ${online && html`<i class="presence"></i>`}
    </div>`
}

/**
 * Every badge an account carries, in a fixed order.
 *
 * A badge says something the server was told by whoever runs it — that this
 * account helped test the app, paid for it, or translated it — so it is drawn
 * exactly where a name is and nowhere else, and it is never a control: there
 * is nothing to press and nothing it unlocks.
 *
 * Anything the app does not recognise is dropped rather than drawn as a
 * mystery mark, which is also what keeps an older build safe against a badge
 * added to the server after it shipped.
 */
const BADGE_ICONS = {
  verified: Icon.verified,
  tester: Icon.tester,
  donor: Icon.donor,
  translator: Icon.translator,
  bot: Icon.bot
}

export function Badges ({ badges = null, size = 15 } = {}) {
  const list = (Array.isArray(badges) ? badges : []).filter((b) => BADGE_ICONS[b])
  if (!list.length) return null
  return list.map((badge) => html`
    <span key=${badge} class=${cx('verified-badge', 'badge-' + badge)} title=${t('badge.' + badge)}>
      ${BADGE_ICONS[badge](size)}
    </span>`)
}

/**
 * An error, in words a person can act on.
 *
 * Most errors from the core are already sentences. The ones with a `reason`
 * are the ones a screen can say something better about — being frozen is not
 * a bug report, and should not read like one.
 */
export function errorText (err, fallback = 'err.generic') {
  if (err && err.reason === 'frozen') return t('frozen.self')
  if (err && err.reason === 'peer-frozen') return t('frozen.peer')
  return (err && err.message) || t(fallback)
}

/* ------------------------------------------------------------------ *
 * Toasts
 * ------------------------------------------------------------------ */

const ToastContext = createContext(null)

export function ToastHost ({ children }) {
  const [items, setItems] = useState([])

  const push = useCallback((message, { error = false, ms = 3200 } = {}) => {
    const id = Math.random().toString(36).slice(2)
    setItems((list) => [...list, { id, message, error }])
    setTimeout(() => setItems((list) => list.filter((i) => i.id !== id)), ms)
  }, [])

  return html`
    <${ToastContext.Provider} value=${push}>
      ${children}
      <div class="toasts" role="status" aria-live="polite">
        ${items.map((item) => html`
          <div key=${item.id} class=${cx('toast', item.error && 'error')}>
            ${item.error ? Icon.info(16) : Icon.check(16)}
            <span>${item.message}</span>
          </div>`)}
      </div>
    <//>`
}

export function useToast () {
  return useContext(ToastContext) || (() => {})
}

/* ------------------------------------------------------------------ *
 * Modal
 * ------------------------------------------------------------------ */

export function Modal ({ title, onClose, children, footer = null, wide = false, dismissable = true }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && dismissable) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, dismissable])

  return html`
    <div class="scrim" onClick=${(e) => { if (dismissable && e.target === e.currentTarget) onClose() }}>
      <div class=${cx('modal', wide && 'wide')} role="dialog" aria-modal="true" aria-label=${title}>
        <div class="modal-head">
          <h2 class="grow">${title}</h2>
          ${dismissable && html`
            <button class="icon-btn" onClick=${onClose} aria-label=${t('action.close')}>${Icon.close(18)}</button>`}
        </div>
        <div class="modal-body">${children}</div>
        ${footer && html`<div class="modal-foot">${footer}</div>`}
      </div>
    </div>`
}

export function ConfirmDialog ({ title, message, confirmLabel, danger = false, onConfirm, onCancel }) {
  return html`
    <${Modal} title=${title} onClose=${onCancel}
      footer=${html`
        <button class="btn btn-ghost" onClick=${onCancel}>${t('action.cancel')}</button>
        <button class=${cx('btn', danger ? 'btn-danger' : 'btn-primary')} onClick=${onConfirm}>
          ${confirmLabel || t('action.confirm')}
        </button>`}>
      <p class="dim">${message}</p>
    <//>`
}

/* ------------------------------------------------------------------ *
 * Context menu
 * ------------------------------------------------------------------ */

export function Menu ({ x, y, items, onClose, align = 'start' }) {
  const ref = useRef(null)
  const [pos, setPos] = useState({ left: x, top: y, visibility: 'hidden' })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const margin = 8
    let left = align === 'end' ? x - rect.width : x
    let top = y
    if (left + rect.width > innerWidth - margin) left = innerWidth - rect.width - margin
    if (left < margin) left = margin
    if (top + rect.height > innerHeight - margin) top = Math.max(margin, y - rect.height)
    setPos({ left, top, visibility: 'visible' })
  }, [x, y, align])

  useEffect(() => {
    const close = () => onClose()
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    // `capture` so a click inside the menu still runs its own handler first.
    setTimeout(() => {
      document.addEventListener('pointerdown', close)
      document.addEventListener('keydown', onKey)
      window.addEventListener('resize', close)
      window.addEventListener('scroll', close, true)
    }, 0)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [onClose])

  return html`
    <div class="popover" ref=${ref} style=${pos} role="menu"
         onPointerDown=${(e) => e.stopPropagation()}>
      ${items.filter(Boolean).map((item, i) => item.separator
        ? html`<div key=${'s' + i} class="menu-sep"></div>`
        : html`
          <button key=${i} class=${cx('menu-item', item.danger && 'danger')} role="menuitem"
                  onClick=${() => { onClose(); item.onSelect() }}>
            ${item.icon || null}
            <span class="grow">${item.label}</span>
            ${item.shortcut && html`<span class="shortcut">${item.shortcut}</span>`}
          </button>`)}
    </div>`
}

/** Hook that owns menu state: `menu.open(event, items)` / `menu.node`. */
export function useMenu () {
  const [state, setState] = useState(null)

  const open = useCallback((event, items, align = 'start') => {
    event.preventDefault()
    event.stopPropagation()
    const rect = event.currentTarget && event.currentTarget.getBoundingClientRect
      ? event.currentTarget.getBoundingClientRect()
      : null
    const x = event.clientX || (rect ? (align === 'end' ? rect.right : rect.left) : 0)
    const y = event.clientY || (rect ? rect.bottom + 4 : 0)
    setState({ x, y, items, align })
  }, [])

  const close = useCallback(() => setState(null), [])

  const node = state
    ? html`<${Menu} x=${state.x} y=${state.y} items=${state.items} align=${state.align} onClose=${close} />`
    : null

  return { open, close, node }
}

/* ------------------------------------------------------------------ *
 * Small controls
 * ------------------------------------------------------------------ */

export function Switch ({ checked, onChange, label = '', disabled = false }) {
  return html`
    <button class=${cx('switch', checked && 'on')} role="switch"
            aria-checked=${String(!!checked)} aria-label=${label} disabled=${disabled}
            onClick=${() => !disabled && onChange(!checked)}></button>`
}

export function SettingRow ({ icon, title, sub, right, onClick, danger = false }) {
  const Tag = onClick ? 'button' : 'div'
  return html`
    <${Tag} class=${cx('list-row', danger && 'danger')} onClick=${onClick}>
      ${icon && html`<span class="glyph">${icon}</span>`}
      <span class="body">
        <span class="title">${title}</span>
        ${sub && html`<span class="sub">${sub}</span>`}
      </span>
      ${right || null}
    <//>`
}

export function Spinner ({ large = false }) {
  return html`<div class=${cx('spinner', large && 'lg')} role="progressbar"></div>`
}

export function Empty ({ icon, title, message, action = null }) {
  return html`
    <div class="empty">
      ${icon && html`<div class="glyph">${icon}</div>`}
      ${title && html`<h3>${title}</h3>`}
      ${message && html`<p>${message}</p>`}
      ${action}
    </div>`
}

export function StatusDot ({ status }) {
  return html`<span class=${cx('status-dot', status)} title=${t('status.' + status)}></span>`
}

/** A value with a copy button — used for IDs, invite links and recovery keys. */
export function CopyField ({ value, label = '', mono = true }) {
  const toast = useToast()
  return html`
    <div class="field">
      ${label && html`<label>${label}</label>`}
      <div class="row gap2">
        <input class=${cx('input', mono && 'mono')} readonly value=${value}
               onFocus=${(e) => e.target.select()} />
        <button class="btn btn-soft" onClick=${async () => {
          const ok = await copyText(value)
          toast(ok ? t('action.copied') : t('err.generic'), { error: !ok })
        }}>${Icon.copy(16)}</button>
      </div>
    </div>`
}

/* ------------------------------------------------------------------ *
 * Emoji picker
 * ------------------------------------------------------------------ */

export const QUICK_REACTIONS = ['👍', '❤️', '🔥', '😂', '😮', '😢', '🙏', '🌲']

/**
 * The emoji picker.
 *
 * One continuous scroller with a sticky heading per category, a search field
 * and a tab strip that both follows the scroll and jumps to a category -
 * which is the arrangement people already know from every other messenger.
 *
 * Two details carry more weight than they look:
 *
 *  - Pointer-down anywhere except the search field is cancelled. The composer
 *    is a contenteditable, and letting a click here move focus would collapse
 *    the caret, so an emoji chosen after writing half a sentence landed at the
 *    start of it or nowhere at all.
 *  - Picking does not close the panel. Reactions come in runs, and reopening
 *    the panel for each one is the kind of friction that makes a feature go
 *    unused.
 */
export function EmojiPicker ({ onPick }) {
  const [query, setQuery] = useState('')
  // Frozen for as long as the panel is open. Live-updating it would grow the
  // recents row under the scroll position while a run of emoji is being
  // picked, and shift everything below out from under the finger.
  const [recent] = useState(loadRecent)
  const [active, setActive] = useState(() => (recent.length ? 'recent' : CATEGORIES[0].id))

  const scroller = useRef(null)
  const sections = useRef({})
  const ticking = useRef(false)

  const groups = useMemo(() => {
    const head = recent.length ? [{ id: 'recent', chars: recent }] : []
    return head.concat(CATEGORIES.map(({ id, chars }) => ({ id, chars })))
  }, [recent])

  const found = query.trim() ? searchEmoji(query) : null

  const pick = useCallback((char) => {
    pushRecent(char)
    onPick(char)
  }, [onPick])

  /** Highlight the tab of whichever category the scroll is currently inside. */
  const onScroll = useCallback(() => {
    if (ticking.current) return
    ticking.current = true
    requestAnimationFrame(() => {
      ticking.current = false
      const box = scroller.current
      if (!box) return
      let current = groups[0] && groups[0].id
      for (const group of groups) {
        const node = sections.current[group.id]
        if (!node) continue
        if (node.offsetTop - 24 <= box.scrollTop) current = group.id
        else break
      }
      setActive(current)
    })
  }, [groups])

  function goTo (id) {
    const box = scroller.current
    const node = sections.current[id]
    setActive(id)
    if (box && node) box.scrollTop = node.offsetTop
  }

  function tabGlyph (id) {
    const category = CATEGORIES.find((c) => c.id === id)
    return category ? category.tab : ''
  }

  return html`
    <div class="emoji-panel"
         onPointerDown=${(event) => {
           // Keep the caret where the person left it; see the note above.
           if (!event.target.closest('input')) event.preventDefault()
         }}>

      <div class="emoji-search">
        ${Icon.search(15)}
        <input value=${query} spellcheck="false" autocapitalize="off"
               placeholder=${t('emoji.search')}
               onInput=${(e) => setQuery(e.target.value)} />
        ${query && html`
          <button class="emoji-clear" onClick=${() => setQuery('')}
                  aria-label=${t('action.close')}>${Icon.close(14)}</button>`}
      </div>

      ${found
        ? html`
          <div class="emoji-scroll">
            ${found.length === 0
              ? html`<p class="emoji-empty">${t('emoji.none')}</p>`
              : html`
                <div class="emoji-grid">
                  ${found.map((char) => html`
                    <button key=${char} onClick=${() => pick(char)}>${char}</button>`)}
                </div>`}
          </div>`
        : html`
          <div class="emoji-scroll" ref=${scroller} onScroll=${onScroll}>
            ${groups.map((group) => html`
              <section key=${group.id} ref=${(node) => { sections.current[group.id] = node }}>
                <h4 class="emoji-title">${t('emoji.cat.' + group.id)}</h4>
                <div class="emoji-grid">
                  ${group.chars.map((char) => html`
                    <button key=${char} onClick=${() => pick(char)}>${char}</button>`)}
                </div>
              </section>`)}
          </div>

          <div class="emoji-tabs">
            ${groups.map((group) => html`
              <button key=${group.id}
                      class=${cx('emoji-tab', group.id === active && 'active')}
                      title=${t('emoji.cat.' + group.id)}
                      aria-label=${t('emoji.cat.' + group.id)}
                      onClick=${() => goTo(group.id)}>
                ${group.id === 'recent' ? Icon.clock(17) : tabGlyph(group.id)}
              </button>`)}
          </div>`}
    </div>`
}

/* ------------------------------------------------------------------ *
 * Lightbox
 * ------------------------------------------------------------------ */

export function Lightbox ({ src, mime, name, onClose, onDownload }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const isVideo = (mime || '').startsWith('video/')

  return html`
    <div class="lightbox">
      <div class="bar">
        <span class="grow truncate">${name}</span>
        ${onDownload && html`
          <button class="icon-btn" onClick=${onDownload} aria-label=${t('action.download')}>
            ${Icon.download(20)}
          </button>`}
        <button class="icon-btn" onClick=${onClose} aria-label=${t('action.close')}>${Icon.close(20)}</button>
      </div>
      <div class="stage" onClick=${(e) => { if (e.target === e.currentTarget) onClose() }}>
        ${isVideo
          ? html`<video src=${src} controls autoplay playsinline></video>`
          : html`<img src=${src} alt=${name} />`}
      </div>
    </div>`
}
